<template>
  <v-card
    id="learningObjectCard"
    :height="isMobileDevice ? windowHeight : '100vh'"
    class="elevation-0"
  >
    <navigation-drawer-desktop
      :title="title"
      :description="description"
      :current-content="currentContent"
      :contents="contents"
      :content-index="contentIndex"
      :class="isMobileDevice ? 'd-none' : 'd-none d-sm-flex'"
      @changeChapter="setContentId"
    />
    <control-bar-desktop
      :disable-prev="disablePrevContentIcon"
      :disable-next="disableNextContentIcon"
      :actual-item="contentIndex + 1"
      :total-items="contentsSize"
      :is-model-loaded="isModelLoaded"
      :is-animation-available="isAnimationAvailable"
      :is-animation-paused="isAnimationPaused"
      :class="isMobileDevice ? 'd-none' : 'd-none d-sm-block'"
      @previous="previousContent"
      @next="nextContent"
      @play="playAnimation"
      @pause="pauseAnimation"
    />
    <menu-button
      :is-animation-available="isAnimationAvailable"
      :is-animation-paused="isAnimationPaused"
      :class="isMobileDevice ? 'd-flex' : 'd-flex d-sm-none'"
      @open-lo-info="isLOInfoBottomSheetOpen = true"
      @activate-ar="activateARViewer = !activateARViewer"
      @play="playAnimation"
      @pause="pauseAnimation"
    />
    <viewer
      ref="viewer"
      :url="url"
      :title="title"
      :poster-url="posterUrl"
      :camera-target="cameraTarget"
      :camera-orbit="cameraOrbit"
      :field-of-view="fieldOfView"
      :activate-ar="activateARViewer"
      @model-loaded="isModelLoaded = true"
      @animation-loaded="onAnimationLoaded"
      @animation-paused="onAnimationPaused"
    />
    <title-toolbar
      :title="title"
      :current-content="currentContent"
      :disable-prev="disablePrevContentIcon"
      :disable-next="disableNextContentIcon"
      :class="isMobileDevice ? 'd-block' : 'd-block d-sm-none'"
      @previous="previousContent"
      @next="nextContent"
      @openChapterInfo="isChapterInfoBottomSheetOpen = true"
    />
    <lo-info-bottom-sheet
      :is-open="isLOInfoBottomSheetOpen"
      :title="title"
      :description="description"
      class="d-flex d-sm-none"
      @close="isLOInfoBottomSheetOpen = false"
    />
    <chapter-info-bottom-sheet
      :current-content="currentContent"
      :is-open="isChapterInfoBottomSheetOpen"
      class="d-flex d-sm-none"
      @close="isChapterInfoBottomSheetOpen = false"
    />
  </v-card>
</template>

<script>
import Viewer from '@/components/Viewer.vue';
import MenuButton from '@/components/mobile/MenuButton.vue';
import LoInfoBottomSheet from '@/components/mobile/LOInfoBottomSheet';
import ChapterInfoBottomSheet from '@/components/mobile/ChapterInfoBottomSheet';
import TitleToolbar from '@/components/mobile/TitleToolbar';
import NavigationDrawerDesktop from '@/components/desktop/NavigationDrawerDesktop';
import ControlBarDesktop from '@/components/desktop/ControlBarDesktop';
import HandleURLHashParams from '@/mixins/HandleURLHashParams';
import { isMobile } from 'mobile-device-detect';
import { getByReferenceId } from '@/services/creator/learningObject';

export default {
  name: 'LearningObject',
  components: {
    Viewer,
    MenuButton,
    LoInfoBottomSheet,
    ChapterInfoBottomSheet,
    TitleToolbar,
    NavigationDrawerDesktop,
    ControlBarDesktop,
  },
  mixins: [HandleURLHashParams],
  data: () => ({
    learningObject: {},
    contents: [],
    currentContent: {},
    contentIndex: 0,
    url: '',
    posterUrl: '',
    title: '',
    loDescriptionBottomSheet: false,
    currentContentDescriptionBottomSheet: false,
    showToolbar: true,
    description: '',
    hotspots: [],
    cameraTarget: 'auto auto auto',
    cameraOrbit: 'auto auto auto',
    fieldOfView: 'auto',
    isLOInfoBottomSheetOpen: false,
    isChapterInfoBottomSheetOpen: false,
    activateARViewer: false,
    isModelLoaded: false,
    windowHeight: null,
    isAnimationAvailable: false,
    isAnimationPaused: false,
    loading: false,
  }),
  computed: {
    disablePrevContentIcon() {
      return (this.contents.length == 0 || this.contentIndex == 0);
    },
    disableNextContentIcon() {
      return (this.contents.length == 0 || this.contentIndex == this.contents.length - 1);
    },
    showToolbarButtonIcon() {
      return this.showToolbar ? 'mdi-chevron-down' : 'mdi-chevron-up';
    },
    contentsSize() {
      return this.contents.length;
    },
    isMobileDevice() {
      return isMobile;
    },
  },
  watch: {
    learningObject(lo) {
      if (!lo) return;
      this.title = lo.name || '';
      this.description = lo.description || '';
      this.$i18n.locale = lo.language || 'pt-BR';
      this.contents = lo.sortedContentIds.map((id) => lo.contents.find((content) => content.id === id));
      this.contentIndex = this.getContentIndexById(this.$route.params.content);
      if (!this.contents.length
        || this.contentIndex < 0
        || this.contentIndex >= this.contents.length
        || !this.contents[this.contentIndex]
        || !this.contents[this.contentIndex].id) return;
      this.setContentId(this.contents[this.contentIndex].id);
    },
    // Handle changes in route's parameter to show a different content
    '$route.params.content': function (contentId) {
      this.loadContent(contentId);
    },
    '$route.params.id': function (id) {
      // no-op, should only run the apollo query automatically
    },
  },
  created() {
    this.getLearningObjectService();
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  methods: {
    handleResize() {
      this.windowHeight = window.innerHeight;
    },
    getContentIndexById(id) {
      if (!id) return 0;
      const response = this.contents.findIndex((content) => content.id === id);
      return response
    },
    setContentId(id) {
      if (this.$route.params.content !== id) {
        this.$router.push({
          name: 'LearningObject',
          params: {
            id: this.$route.params.id,
            content: id,
          },
        });
      } else if (this.currentContent.id !== id) {
        this.loadContent(id);
      } else {
        // Recenter camera if clicked on the current content's tab
        this.$refs.viewer.resetCamera();
      }
    },
    loadContent(id) {
      this.contentIndex = this.getContentIndexById(id);
      if (!this.contents.length
        || this.contentIndex < 0
        || this.contentIndex >= this.contents.length
        || !this.contents[this.contentIndex]) return;
      this.currentContent = this.contents[this.contentIndex];
      // Get glb url (CloudFront's CDN url)
      this.url = this.currentContent.content.url;
      // Get poster url (CloudFront's CDN url)
      this.posterUrl = this.currentContent.content.posterUrl;
      // Load new hotspots, if available
      this.$refs.viewer.setHotspots(this.currentContent.hotspots || []);
      this.cameraTarget = this.currentContent.cameraTarget;
      this.cameraOrbit = this.currentContent.cameraOrbit;
      this.fieldOfView = this.currentContent.fieldOfView;
    },
    nextContent() {
      if (!this.contents.length
        || (this.contentIndex + 1) < 0
        || (this.contentIndex + 1) >= this.contents.length
        || !this.contents[this.contentIndex + 1]
        || !this.contents[this.contentIndex + 1].id) return;
      this.setContentId(this.contents[++this.contentIndex].id);
    },
    previousContent() {
      if (!this.contents.length
        || (this.contentIndex - 1) < 0
        || (this.contentIndex - 1) >= this.contents.length
        || !this.contents[this.contentIndex - 1]
        || !this.contents[this.contentIndex - 1].id) return;
      this.setContentId(this.contents[--this.contentIndex].id);
    },
    onAnimationLoaded(value) {
      this.isAnimationAvailable = !!value;
    },
    onAnimationPaused(value) {
      this.isAnimationPaused = value;
    },
    playAnimation() {
      this.$refs.viewer.play();
    },
    pauseAnimation() {
      this.$refs.viewer.pause();
    },
    async getLearningObjectService() {
      this.loading = true;
      const response = await getByReferenceId(this.$route.params.id)
      this.loading = false;
      this.learningObject = response.data
    }
  },
};
</script>
